import { StaticRevisionReference } from '@edgebox/data-definition-kit';
import { TaxonomyCategory, TaxonomyFacet, SyncCoreDataDefinitionsEnumTranslator } from '@edgebox/sync-core-data-definitions';
import { ClientContentItemEntity, ClientTaxonomyTermEntity, ISyncCoreApi, ManageContentItemTermsDto } from '@edgebox/sync-core-rest-client';
import { faChevronDown, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import {
  BP_POPOVER_CLASSES,
  BP_PRIMARY_BUTTON_CLASSES,
  BP_TOOLBAR_BUTTON_CLASSES,
  BP_TOOLBAR_BUTTON_OPEN_CLASSES,
} from '../../../Components/brandPresenceClasses';
import { IFilterDropdownOption, IFilterDropdownOptionGroup } from './FilterDropdownList';
import { MultiLevelDropdownPanel } from './MultiLevelDropdownPanel';
import { getCachedTaxonomies, getCachedTaxonomyTermsByFacet } from './useCachedTaxonomyData';
import { useDismissOnOutsideClick } from '../../../Components/useDismissOnOutsideClick';

type ActionMode = 'add' | 'remove';

interface ITermTarget {
  id: string;
  label: string;
  taxonomyFacet: TaxonomyFacet;
  category?: TaxonomyCategory;
}

const TARGET_SEARCH_PLACEHOLDER = 'Search taxonomies';
const VALUE_SEARCH_PLACEHOLDER = 'Search tags';

export function ManageTermsActionDropdown({
  api,
  selectedItems,
  mode,
  onExecuted,
}: {
  api?: ISyncCoreApi;
  selectedItems: ClientContentItemEntity[];
  mode: ActionMode;
  onExecuted?: () => void;
}) {
  const [open, setOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const [targetSearch, setTargetSearch] = React.useState('');
  const [valueSearch, setValueSearch] = React.useState('');
  const [selectedTargetId, setSelectedTargetId] = React.useState<string | undefined>(undefined);
  const [targets, setTargets] = React.useState<ITermTarget[]>([]);
  const [loadingTargets, setLoadingTargets] = React.useState(false);
  const [loadingValues, setLoadingValues] = React.useState(false);
  const [valueOptions, setValueOptions] = React.useState<IFilterDropdownOption[]>([]);
  const [valueEntityById, setValueEntityById] = React.useState<Map<string, ClientTaxonomyTermEntity>>(new Map());
  const [selectedTermIdsByTargetId, setSelectedTermIdsByTargetId] = React.useState<Record<string, string[]>>({});
  const [selectedTermsByTargetId, setSelectedTermsByTargetId] = React.useState<Record<string, ClientTaxonomyTermEntity[]>>({});

  const selectedTarget = React.useMemo(
    () => (selectedTargetId ? targets.find((target) => target.id === selectedTargetId) : undefined),
    [selectedTargetId, targets]
  );

  const selectedTermCount = React.useMemo(
    () => Object.values(selectedTermIdsByTargetId).reduce((sum, ids) => sum + ids.length, 0),
    [selectedTermIdsByTargetId]
  );

  React.useEffect(() => {
    if (!open || !api) {
      return;
    }

    let active = true;
    void (async () => {
      setLoadingTargets(true);

      const taxonomies = await getCachedTaxonomies(api);
      if (!active) {
        return;
      }

      const nextTargets = taxonomies
        .filter((taxonomy) => !!taxonomy.facet)
        .map((taxonomy) => ({
          id: `taxonomy:${taxonomy.facet as string}`,
          label: taxonomy.name,
          taxonomyFacet: taxonomy.facet as TaxonomyFacet,
          category: taxonomy.category as TaxonomyCategory,
        }));

      setTargets(nextTargets);
      setLoadingTargets(false);
    })();

    return () => {
      active = false;
    };
  }, [api, open]);

  React.useEffect(() => {
    if (!open || !api || !selectedTarget) {
      return;
    }

    let active = true;
    void (async () => {
      setLoadingValues(true);

      const terms = await getCachedTaxonomyTermsByFacet(api, selectedTarget.taxonomyFacet);
      if (!active) {
        return;
      }

      const nextValueOptions = terms
        .map((term) => ({
          id: String(term.id || '').trim(),
          label: String(term.name || '').trim(),
        }))
        .filter((option) => option.id.length > 0 && option.label.length > 0);

      const nextValueEntityById = new Map<string, ClientTaxonomyTermEntity>();
      for (const term of terms) {
        const termId = String(term.id || '').trim();
        if (!termId) {
          continue;
        }

        nextValueEntityById.set(termId, term);
      }

      setValueOptions(nextValueOptions);
      setValueEntityById(nextValueEntityById);
      setLoadingValues(false);
    })();

    return () => {
      active = false;
    };
  }, [api, open, selectedTarget]);

  const filteredTargets = React.useMemo(() => {
    const needle = targetSearch.trim().toLowerCase();
    if (!needle) {
      return targets;
    }

    return targets.filter((target) => target.label.toLowerCase().includes(needle));
  }, [targetSearch, targets]);

  const groupedFilteredTargets = React.useMemo((): IFilterDropdownOptionGroup[] => {
    const groups = new Map<string, IFilterDropdownOptionGroup>();

    for (const target of filteredTargets) {
      const groupId = target.category || 'taxonomy';
      const groupLabel = SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue('TaxonomyCategory', groupId as TaxonomyCategory);
      const existing = groups.get(groupId);
      if (existing) {
        existing.options.push({ id: target.id, label: target.label });
      } else {
        groups.set(groupId, {
          id: groupId,
          label: groupLabel,
          options: [{ id: target.id, label: target.label }],
        });
      }
    }

    return Array.from(groups.values()).sort((left, right) => left.label.localeCompare(right.label));
  }, [filteredTargets]);

  const filteredValueOptions = React.useMemo(() => {
    const needle = valueSearch.trim().toLowerCase();
    if (!needle) {
      return valueOptions;
    }

    return valueOptions.filter((option) => option.label.toLowerCase().includes(needle));
  }, [valueOptions, valueSearch]);

  const selectedTargetOptions = React.useMemo(() => {
    return targets
      .filter((target) => (selectedTermIdsByTargetId[target.id] || []).length > 0)
      .map((target) => ({
        id: target.id,
        label: target.label,
        badgeCount: (selectedTermIdsByTargetId[target.id] || []).length,
        interactive: true,
      }));
  }, [selectedTermIdsByTargetId, targets]);

  const selectedValueOptions = React.useMemo(() => {
    if (!selectedTargetId) {
      return [];
    }

    const selectedIds = new Set(selectedTermIdsByTargetId[selectedTargetId] || []);
    return valueOptions.filter((option) => selectedIds.has(option.id));
  }, [selectedTargetId, selectedTermIdsByTargetId, valueOptions]);

  const canExecute = !!api && selectedItems.length > 0 && selectedTermCount > 0 && !submitting;

  const clearPanelState = () => {
    setOpen(false);
    setSelectedTargetId(undefined);
    setTargetSearch('');
    setValueSearch('');
  };

  const toggleValue = (option: IFilterDropdownOption) => {
    if (!selectedTargetId) {
      return;
    }

    const entity = valueEntityById.get(option.id);
    if (!entity) {
      return;
    }

    setSelectedTermIdsByTargetId((previous) => {
      const next = { ...previous };
      const current = new Set(next[selectedTargetId] || []);
      if (current.has(option.id)) {
        current.delete(option.id);
      } else {
        current.add(option.id);
      }

      const nextIds = Array.from(current);
      if (nextIds.length > 0) {
        next[selectedTargetId] = nextIds;
      } else {
        delete next[selectedTargetId];
      }

      return next;
    });

    setSelectedTermsByTargetId((previous) => {
      const next = { ...previous };
      const current = new Map((next[selectedTargetId] || []).map((term) => [String(term.id), term]));
      if (current.has(option.id)) {
        current.delete(option.id);
      } else {
        current.set(option.id, entity);
      }

      const nextTerms = Array.from(current.values());
      if (nextTerms.length > 0) {
        next[selectedTargetId] = nextTerms;
      } else {
        delete next[selectedTargetId];
      }

      return next;
    });
  };

  const execute = async () => {
    if (!canExecute || !api) {
      return;
    }

    setSubmitting(true);
    setError(undefined);

    try {
      const terms = Object.values(selectedTermsByTargetId).flat();
      const uniqueTermsById = new Map<string, ClientTaxonomyTermEntity>();
      for (const term of terms) {
        const id = String(term.id || '').trim();
        if (!id) {
          continue;
        }

        uniqueTermsById.set(id, term);
      }

      const termReferences = Array.from(uniqueTermsById.values()).map((term) => new StaticRevisionReference(term));
      if (!termReferences.length) {
        throw new Error('No terms selected.');
      }

      await Promise.all(
        selectedItems.map((item) =>
          api.governance.contentItems.manageTerms(
            item.id,
            new ManageContentItemTermsDto(mode === 'add' ? { add: termReferences } : { remove: termReferences })
          )
        )
      );

      setSelectedTermIdsByTargetId({});
      setSelectedTermsByTargetId({});
      clearPanelState();
      onExecuted?.();
    } catch (executionError) {
      setError((executionError as Error)?.message || 'Unable to update terms for selected content items.');
    } finally {
      setSubmitting(false);
    }
  };

  // The action itself is chosen beside this control; this one only asks which tags.
  const title = selectedTermCount > 0 ? `${selectedTermCount} ${selectedTermCount === 1 ? 'tag' : 'tags'} chosen` : 'Choose tags';
  const tagCountLabel = selectedTermCount === 1 ? '1 tag' : `${selectedTermCount || ''} tags`.trim();
  const executeLabel = mode === 'add' ? `Add ${tagCountLabel}` : `Remove ${tagCountLabel}`;

  useDismissOnOutsideClick({ open, containerRef, triggerRef, onDismiss: clearPanelState });

  return (
    <div className="relative flex flex-col items-end gap-1">
      <div className="relative flex items-center gap-2" ref={containerRef}>
        <button
          ref={triggerRef}
          type="button"
          className={`${open ? BP_TOOLBAR_BUTTON_OPEN_CLASSES : BP_TOOLBAR_BUTTON_CLASSES} min-w-[170px] justify-between motion-safe:animate-bp-fade-in`}
          aria-expanded={open}
          aria-haspopup="true"
          onClick={() => {
            if (open) {
              clearPanelState();
              return;
            }

            setOpen(true);
          }}
          disabled={submitting}
        >
          <span>{title}</span>
          <FontAwesomeIcon icon={faChevronDown} className={`text-xs transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
        </button>

        <button
          type="button"
          className={BP_PRIMARY_BUTTON_CLASSES}
          disabled={!canExecute}
          onClick={() => {
            void execute();
          }}
        >
          {submitting ? <FontAwesomeIcon icon={faSpinner} spin className="mr-2" /> : null}
          {executeLabel}
        </button>

        {open ? (
          <div className={`absolute right-0 top-full z-20 mt-2 w-[360px] ${BP_POPOVER_CLASSES}`}>
            <MultiLevelDropdownPanel
              selectedTarget={selectedTarget ? { id: selectedTarget.id, label: selectedTarget.label } : undefined}
              targetSearch={targetSearch}
              onTargetSearchChange={setTargetSearch}
              targetSearchPlaceholder={TARGET_SEARCH_PLACEHOLDER}
              selectedTargetOptions={selectedTargetOptions}
              filteredTargetOptions={filteredTargets.map((target) => ({ id: target.id, label: target.label }))}
              groupedFilteredTargetOptions={groupedFilteredTargets}
              loadingTargets={loadingTargets}
              onSelectTarget={(option) => {
                setSelectedTargetId(option.id);
                setValueSearch('');
              }}
              valueSearch={valueSearch}
              onValueSearchChange={setValueSearch}
              valueSearchPlaceholder={VALUE_SEARCH_PLACEHOLDER}
              selectedValueOptions={selectedValueOptions}
              filteredValueOptions={filteredValueOptions}
              loadingValues={loadingValues}
              valueSelectionMultiple={true}
              onToggleValue={toggleValue}
              hideSelectedValuesFromFiltered={true}
              changeTargetLabel="Change taxonomy"
              onBackToTargets={() => {
                setSelectedTargetId(undefined);
                setValueSearch('');
              }}
              onCancel={() => {
                clearPanelState();
              }}
              onApply={() => {
                clearPanelState();
              }}
              applyLabel="Select"
            />
          </div>
        ) : null}
      </div>

      {error ? <div className="text-sm text-danger">{error}</div> : null}
    </div>
  );
}
